/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { KeyValue } from '@angular/common';
import { WizardDTO } from '@api/models/';
import { ThemeCode } from '@core/models';
import { RouteCodes } from '@core/config/route-codes';
import { QuotationDataModel } from '../quotation';
import { EditableParam } from '../quotation-calculate';
import { StartDestination, NextDestination } from '../wizard';
import { PROVIDER } from './../recognition';

export interface AppConfigModel {
  /**
   * configurazione per la grafica
   */
  theme: {
    /**
     * flag per determinare se il logo sull'header è visibile
     */
    logoOnHeader?: boolean;

    /**
     * * testo primario del footer
     */
    footerPrimaryText?: string;

    /**
     * testo secondario del footer
     */
    footerSecondaryText?: string;

    /**
     * url del logo principale
     */
    logoUrl?: string;
  };

  /**
   * configurazioni generiche
   */
  generic: {
    /**
     * effettua redirect verso pagina di errore quando le chiamate vanno in 401
     */
    redirectUnauthorized?: boolean;

    /**
     * codice errore per cui è possibile impostare un redirect
     */
    errorsWithRedirect?: { [errorCode: string]: RouteCodes };

    /**
     * modalità di visualizzazione degli errori
     */
    errorDisplay?: 'complete' | 'base';

    /**
     * numero massimo di tentavi consentiti a seguito di un errore di un'api
     */
    maxAttempsAfterError?: number;

    /**
     * link di aiuto
     */
    helpLink?: string;

    /**
     * link della FAQ
     */
    faqLink?: string;

    /**
     * se true invia i log tramite api dedicata
     */
    sendLogs?: boolean;
  };

  /**
   * configurazioni di sviluppo
   */
  dev: {
    operationCodes?: KeyValue<string, string>[];
  };

  /**
   * configurazione per gestione security
   */
  security: {
    /**
     * nome dell'header http su cui viene applicato il request token
     */
    authHeaderName?: string;

    /**
     * nome della chiave con cui viene salvato il request token su storage
     */
    authStorageKeyName?: string;

    /**
     * elenco di url su cui non deve essere applicato l'header di autenticazione
     */
    urlOutOfAuth?: string[];

    /**
     * l'autenticazione richiede la presenza del query param "code" nell'url di ingresso
     * alla webapp. Il valore viene inviato all'authenticate attraverso la proprietà "uuid"
     */
    authCodeRequired?: boolean;

    /**
     * abilita la funzione di logout
     */
    logoutEnabled?: boolean;
  };

  /**
   * configurazione del wizard
   */
  wizard: {
    /**
     * consente di ripetere il wizard una volta concluso
     */
    allowRestart?: boolean;

    /**
     * definisce da che pagina partire all'ingresso del wizard
     * - welcome: pagina di benvenuto con messaggio
     * - summary: pagina di riepilogo del wizard
     * - first-open-step: primo step da completare
     */
    startPage?: StartDestination;

    /**
     * definisce la pagina successiva al completamento di un step
     * - summary: pagina di riepilogo con gli step completati e quelli da completare;
     * - first-open-step: primo step da completare
     */
    nextPage?: NextDestination;

    /**
     * abilita la navigazione libera del wizard dalla schermata di riepilogo
     */
    freeNavigation?: boolean;

    /**
     * abilita il pulsante per visualizzare la finestra dello stato del processo
     */
    allowStateMenu?: boolean;

    /**
     * modalità con cui viene gestita la chiusura dei task;
     * - wizard: tramite api /positions/{positionId}/wizards/{wizardCode}/steps/{stepCode}/next
     * - workflow: tramite api /workflow/tasks/close/{positionId}
     */
    closingMode?: 'wizard' | 'workflow';

    /**
     * configurazione per wizard "finti"
     */
    dummyWizards?: { [code: string]: WizardDTO };

    /**
     * disabilita il back dallo step del wizard
     */
    avoidBack?: boolean;

    /**
     * è possibile associare ad un codice wizard un tema
     */
    themes?: { [code: string]: ThemeCode };

    /**
     * definisce lo stile della progress bar all'interno degli step:
     * relative: mostra l'avanzamento rispetto ai sotto step
     * absolute: mostra l'avanzamento rispetto al wizard
     */
    progressBar?: 'relative' | 'absolute';
  };

  /**
   * configurazioni dei survey
   */
  survey: {
    /**
     * tempo di debounce sul check sui campi dei form dinamici
     */
    debounceTime?: number;

    /**
     * visualizza i messaggi di warning al continua
     */
    alertWhenContinue?: boolean;

    /**
     * flag per utilizzare lo step code come page code
     */
    stepCodeAsCode?: boolean;

    /**
     * se true non viene applicata la divisione in colonne dei campi
     */
    fixedCol?: boolean;
  };

  /**
   * configurazione dei form
   */
  form: {
    /**
     * tempo di debounce sul check sui campi dei form dinamici
     */
    debounceTime?: number;

    /**
     * visualizza i messaggi di warning al continua
     */
    alertWhenContinue?: boolean;

    /**
     * flag per utilizzare lo step code come page code
     */
    stepCodeAsCode?: boolean;

    /**
     * se true non viene applicata la divisione in colonne dei campi
     */
    fixedCol?: boolean;
  };

  /**
   * configurazione upload file
   */
  upload: {
    /**
     * nome dell'header che definisce il file name nell'api di download
     */
    fileNameHeader?: string;

    /**
     * numero massimo di tentativi consentiti per l'upload di un documento a seguito di un errore
     */
    maxAllowedUploadAttempts?: number;

    /**
     * score minimo per ritenere un documento scansionato da hawk come valido
     * min = 0; max = 1
     */
    minHawkScore?: number;

    /**
     * ordine con cui vengono richiesti all'utente i documenti da caricare
     */
    uploadSorting?: string[];

    /**
     * ordine con cui vengono richiesti all'utente i documenti da validare
     */
    checkSorting?: string[];

    /**
     * pagine richieste per gli upload dei documenti
     */
    documentPages?: { [docCode: string]: string };

    /**
     * modalità di acquisione dei documenti
     */
    acquisitionMode?: 'base' | 'mitek';

    /**
     * numero massimo di pagina che si possono aggiungere
     */
    additionalPages?: { [docCode: string]: number };

    /**
     * mostra suggerimento della pagina da caricare (fronte, retro, o numero)
     */
    pageHints?: boolean;

    /**
     * se true non mostra la pagina di istruzioni per effettuare la foto
     */
    avoidInstructions?: boolean;

    /**
     * attiva visualizzazione a griglia nella schermata di controllo file aggiunti
     */
    gridOnPreview?: boolean;

    /**
     * esegue la scansione dei documenti caricati
     */
    runScan?: boolean;
  };

  /**
   * configurazione labels
   */
  labels: {
    /**
     * url per caricare le label fuori da autenticazione
     */
    openLabelsUrl?: string;

    /**
     * è possibile filtrare le label caricate;
     * nelle key delle label verrà ricercato il pezzo contenuto tra []:
     * se non è presente la label verrà considerata,
     * se è presente la label verrà considerata solo se il valore corrisponde a quanto impostato qui
     */
    filter?: KeyValue<string, string>;
  };

  /**
   * configurazione riconoscimento
   */
  recognition: {
    /**
     * configurazione per impostare il provider dei metodi di riconoscimento
     */
    methodProviders?: { [key: string]: PROVIDER };

    /**
     * metodo di riconoscimento consigliato
     */
    recommendedMethod?: string;

    /**
     * numero massimo di tentativi
     */
    maxAttemps?: number;
  };

  /**
   * firma digitale
   */
  digitalSignature: {
    /**
     * è possibile specificare lo stato che deve avere la pratica
     * per considerare la verifica della firma come completa;
     * utilizzare formato <fase>|<stato> (es: "100|RIT")
     */
    positionMustBe?: [string, string][];
  };

  /**
   * impegni
   */
  obligations: {
    /**
     * determina se è richiesta la conferma esplicita dell'estinzione degli impegni
     */
    explicitConfirmation?: boolean;
  };

  /**
   * visualizzazione preventivo
   */
  quotation: {
    /**
     * dati primari da visualizzare nella card del preventivo
     */
    primaryData?: QuotationDataModel[];

    /**
     * dati secondari da visualizzare nella card del preventivo
     */
    secondaryData?: QuotationDataModel[];

    /**
     * Parametri che è possibile modificare nella conferma preventivo.
     * Implicitamente stabilisce se è possibile modificare il preventivo
     */
    editableParams?: EditableParam[];

    /**
     * abilita il rifiuta preventivo
     */
    rejectAllowed?: boolean;

    /**
     * tipo di visualizzazione
     */
    view?: 'step-by-step' | 'inline';

    /**
     * codici modulo da visionare per il preventivo
     */
    moduleCodes?: string[];

    /**
     * codici modulo la cui visione è obbligatoria
     */
    moduleCodesToView?: string[];

    /**
     * codici modulo la cui presa visione è obbligatoria
     */
    moduleCodesToMark?: string[];
  };

  /**
   * richiesta documenti
   */
  documentsRequest: {
    /**
     * codici per identificare le operazioni di richiesta documentazione aggiuntiva
     */
    additionalRequestOperationCodes?: string[];

    /**
     * definisce un prefisso da aggiungere alla form page di verifica dei documenti
     */
    checkFormPrefix?: string;
  };

  /**
   * calcolo preventivo
   */
  quotationCalculate: {
    /**
     * lista delle sezioni da mostrare prima del calcolo
     */
    preparationSections?: EditableParam[];

    /**
     * sezioni che è possibile modificare dopo il calcolo
     */
    editableSections?: EditableParam[];

    /**
     * regola la visualizzazione dei dettagli:
     * true: vengono mostrati direttamente sotto il preventivo
     * false: compare un bottone per aprire pannello con i dettagli
     */
    detailsInline?: boolean;
  };

  /**
   * dashboard
   */
  dashboard: {};
}
